import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Maximize2, RefreshCw } from 'lucide-react';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import { SupportTicketListItem, SupportTicketThread, SUPPORT_TICKET_PRIORITY_OPTIONS, SUPPORT_TICKET_STATUS_OPTIONS, SUPPORT_TICKET_TYPE_OPTIONS } from '@/components/support/SupportTicketShared';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { useToast } from '@/hooks/useToast';
import { usePageContext } from '@/hooks/usePageContext';
import { getErrorMessage } from '@/services/apiClient';
import type { FilterConfig, FilterOption } from '@/types/filters.types';
import {
  buildSupportTicketAccountSelectOptions,
  buildSupportTicketAssigneeSelectOptions,
  getSupportTicketSummary,
  listSupportTicketAccounts,
  listSupportTicketAssignees,
  listSupportTickets,
  type SupportTicketStatus,
} from '@/services/supportTickets.service';

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Ticket, subject, requester…' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses' },
  { key: 'priority', label: 'Priority', type: 'select', allLabel: 'All priorities' },
  { key: 'type', label: 'Type', type: 'select', allLabel: 'All types' },
  { key: 'accountId', label: 'Account', type: 'select', allLabel: 'All accounts', searchable: true },
  { key: 'assignedTo', label: 'Assigned To', type: 'select', allLabel: 'All assignees', searchable: true },
];

export function SupportTicketsAdmin() {
  const toast = useToast();
  const filterState = useLocalDashboardFilters(FILTER_CONFIGS, 'support-tickets-admin');
  const filterValues = filterState.values;

  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);

  const selectTicket = useCallback((id: number) => {
    setSelectedTicketId(id);
    setPanelExpandTrigger((t) => t + 1);
  }, []);

  const search = (filterValues.search as string[])[0] ?? '';
  const statusArr = filterValues.status as string[];
  const priorityArr = filterValues.priority as string[];
  const typeArr = filterValues.type as string[];
  const assignedToArr = filterValues.assignedTo as string[];
  const status = statusArr.join(',');
  const priority = priorityArr.join(',');
  const type = typeArr.join(',');
  const assignedTo = assignedToArr.join(',');
  const accountIdFilter = (filterValues.accountId as string[])[0] ?? '';

  useEffect(() => {
    setPage(1);
  }, [search, status, priority, type, accountIdFilter, assignedTo, unreadOnly]);

  const ticketListQuery = useQuery({
    queryKey: ['support-ticket-list', 'admin', search, status, priority, type, accountIdFilter, assignedTo, unreadOnly, page],
    queryFn: () =>
      listSupportTickets({
        search: search || undefined,
        status: status || undefined,
        priority: priority || undefined,
        type: type || undefined,
        accountID: accountIdFilter ? Number(accountIdFilter) : undefined,
        assignedToUserId: assignedTo || undefined,
        unreadOnly: unreadOnly || undefined,
        perPage: 50,
        page,
      }),
  });

  const ticketMeta = ticketListQuery.data?.meta ?? {};
  const totalTickets = ticketMeta.total ?? 0;
  const currentPage = ticketMeta.page ?? page;
  const currentPerPage = ticketMeta.perPage ?? 50;

  const summaryQuery = useQuery({
    queryKey: ['support-ticket-summary', 'admin', search, priority, type, accountIdFilter, assignedTo],
    queryFn: () =>
      getSupportTicketSummary({
        search: search || undefined,
        priority: priority || undefined,
        type: type || undefined,
        accountID: accountIdFilter ? Number(accountIdFilter) : undefined,
        assignedToUserId: assignedTo || undefined,
      }),
  });

  const scopedAccountId = accountIdFilter ? Number(accountIdFilter) : undefined;
  const assigneesQuery = useQuery({
    queryKey: ['support-ticket-assignees', scopedAccountId ?? 0],
    queryFn: () => listSupportTicketAssignees(undefined, scopedAccountId),
  });

  const accountsQuery = useQuery({
    queryKey: ['support-ticket-accounts'],
    queryFn: () => listSupportTicketAccounts(),
  });

  useEffect(() => {
    if (!ticketListQuery.isError) return;
    toast.error('Unable to load support queue', getErrorMessage(ticketListQuery.error));
  }, [ticketListQuery.error, ticketListQuery.isError, toast]);

  useEffect(() => {
    if (!ticketListQuery.data?.data.length) {
      setSelectedTicketId(null);
      return;
    }

    if (selectedTicketId == null || !ticketListQuery.data.data.some((ticket) => ticket.supportTicketId === selectedTicketId)) {
      setSelectedTicketId(ticketListQuery.data.data[0].supportTicketId);
    }
  }, [selectedTicketId, ticketListQuery.data]);

  const filterOptions = useMemo<Record<string, FilterOption[]>>(() => {
    const statusOpts: FilterOption[] = SUPPORT_TICKET_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
    const priorityOpts: FilterOption[] = SUPPORT_TICKET_PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
    const typeOpts: FilterOption[] = SUPPORT_TICKET_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
    const accountOpts: FilterOption[] = buildSupportTicketAccountSelectOptions(accountsQuery.data ?? []);
    const assigneeOpts: FilterOption[] = [
      { value: 'unassigned', label: 'Unassigned' },
      ...buildSupportTicketAssigneeSelectOptions(assigneesQuery.data ?? []).map((o) => ({
        value: String(o.value),
        label: o.label,
      })),
    ];

    return {
      status: statusOpts,
      priority: priorityOpts,
      type: typeOpts,
      accountId: accountOpts,
      assignedTo: assigneeOpts,
    };
  }, [accountsQuery.data, assigneesQuery.data]);

  const summary = summaryQuery.data;

  const onTicketUpdated = useCallback(() => {
    void ticketListQuery.refetch();
    void summaryQuery.refetch();
  }, [ticketListQuery, summaryQuery]);

  const activeStatus: SupportTicketStatus | null =
    statusArr.length === 1 ? (statusArr[0] as SupportTicketStatus) : null;

  const selectStatusCard = useCallback(
    (next: SupportTicketStatus) => {
      setUnreadOnly(false);
      if (statusArr.length === 1 && statusArr[0] === next) {
        filterState.clearFilter('status');
      } else {
        filterState.setFilter('status', [next]);
      }
    },
    [statusArr, filterState],
  );

  const selectTotalCard = useCallback(() => {
    setUnreadOnly(false);
    filterState.clearFilter('status');
  }, [filterState]);

  const selectUnreadCard = useCallback(() => {
    setUnreadOnly((prev) => !prev);
  }, []);

  // ─── Feed page context to the AI sidebar ───────────────────────────────────
  const updatePageContext = usePageContext('Support Tickets');
  const visibleTickets = ticketListQuery.data?.data ?? [];
  const selectedTicket = visibleTickets.find((t) => t.supportTicketId === selectedTicketId) ?? null;

  useEffect(() => {
    const filterParts: string[] = [];
    if (search) filterParts.push(`search="${search}"`);
    if (status) filterParts.push(`status=${status}`);
    if (priority) filterParts.push(`priority=${priority}`);
    if (accountIdFilter) filterParts.push(`accountID=${accountIdFilter}`);
    if (assignedTo) filterParts.push(`assignedTo=${assignedTo}`);
    if (unreadOnly) filterParts.push('unreadOnly=true');
    const filters = filterParts.length ? filterParts.join(', ') : 'none';

    const summaryParts: string[] = [];
    if (summary) {
      summaryParts.push(
        `Total ${summary.total}`,
        `Open ${summary.open}`,
        `In progress ${summary.inProgress}`,
        `Observation ${summary.observation}`,
        `Pending customer ${summary.pendingCustomer}`,
        `Resolved ${summary.resolved}`,
        `Unread ${summary.unread}`,
      );
    }
    summaryParts.push(`Showing ${visibleTickets.length} ticket${visibleTickets.length === 1 ? '' : 's'} in current view`);

    const ticketLines = visibleTickets.slice(0, 15).map((t) => {
      const assignee = t.assignedTo?.displayName ?? 'Unassigned';
      const last = t.lastMessageAt ? ` last:${t.lastMessageAt}` : '';
      return `#${t.ticketNumber} [${t.status}/${t.priority}] "${t.subject}" — ${t.accountName} — ${t.requesterName || '?'} — assigned:${assignee}${last}`;
    });

    const extraParts = [`Tickets (up to 15):\n${ticketLines.join('\n') || '(none)'}`];
    if (selectedTicket) {
      const lastMsg = selectedTicket.messages?.[selectedTicket.messages.length - 1];
      extraParts.push(
        `Currently selected: #${selectedTicket.ticketNumber} "${selectedTicket.subject}" (${selectedTicket.status}/${selectedTicket.priority}) — account ${selectedTicket.accountName}, requester ${selectedTicket.requesterName}` +
          (lastMsg ? `\nLast message (${lastMsg.authorRole}): ${(lastMsg.body ?? '').slice(0, 400)}` : ''),
      );
    }

    updatePageContext({
      filters,
      dataSummary: summaryParts.join(' · '),
      extra: extraParts.join('\n\n'),
    });
  }, [
    search,
    status,
    priority,
    accountIdFilter,
    assignedTo,
    unreadOnly,
    summary,
    visibleTickets,
    selectedTicket,
    updatePageContext,
  ]);

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={filterValues}
            options={filterOptions}
            onChange={filterState.setFilter}
            onClear={filterState.clearFilter}
            showAIAssistant={false}
            defaultCollapsed={false}
            storageKey="support-tickets-admin"
            searchCommitMode="blur-or-enter"
          />
        )}
        rightPanel={selectedTicketId != null ? (
          <DetailRightPanel expandTrigger={panelExpandTrigger}>
            <div className="flex h-full min-h-0 w-full min-w-0 flex-col bg-white">
              <div className="flex items-center justify-end gap-1 border-b border-slate-200 px-3 py-1.5">
                <Button variant="ghost" size="sm" onClick={() => setModalOpen(true)}>
                  <Maximize2 className="mr-1 h-3.5 w-3.5" />
                  Expand
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {/* Keyed so a draft never carries over to the next ticket's requester. */}
                <SupportTicketThread
                  key={selectedTicketId}
                  ticketId={selectedTicketId}
                  managerView
                  compact
                  onUpdated={onTicketUpdated}
                  onMergedTargetClick={selectTicket}
                />
              </div>
            </div>
          </DetailRightPanel>
        ) : undefined}
      >
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <PageHeader
            title="Support Tickets"
            actions={(
              <Button
                variant="ghost"
                leftIcon={<RefreshCw className="h-4 w-4" />}
                onClick={onTicketUpdated}
              >
                Refresh
              </Button>
            )}
          />

          <div className="grid gap-2 sm:grid-cols-4 lg:grid-cols-8">
            <SummaryCard
              title="Total"
              value={summary?.total ?? 0}
              active={activeStatus == null && !unreadOnly}
              onClick={selectTotalCard}
            />
            <SummaryCard
              title="Open"
              value={summary?.open ?? 0}
              active={activeStatus === 'open'}
              onClick={() => selectStatusCard('open')}
            />
            <SummaryCard
              title="In Progress"
              value={summary?.inProgress ?? 0}
              active={activeStatus === 'in_progress'}
              onClick={() => selectStatusCard('in_progress')}
            />
            <SummaryCard
              title="Observation"
              value={summary?.observation ?? 0}
              active={activeStatus === 'observation'}
              onClick={() => selectStatusCard('observation')}
            />
            <SummaryCard
              title="On Hold"
              value={summary?.onHold ?? 0}
              active={activeStatus === 'on_hold'}
              onClick={() => selectStatusCard('on_hold')}
            />
            <SummaryCard
              title="Pending Customer"
              value={summary?.pendingCustomer ?? 0}
              active={activeStatus === 'pending_customer'}
              onClick={() => selectStatusCard('pending_customer')}
            />
            <SummaryCard
              title="Resolved"
              value={summary?.resolved ?? 0}
              active={activeStatus === 'resolved'}
              onClick={() => selectStatusCard('resolved')}
            />
            <SummaryCard
              title="Unread"
              value={summary?.unread ?? 0}
              highlight
              active={unreadOnly}
              onClick={selectUnreadCard}
            />
          </div>

          <div className="space-y-3">
            {ticketListQuery.isLoading && (
              <div className="py-10 text-center text-sm text-slate-500">Loading support queue…</div>
            )}

            {!ticketListQuery.isLoading && (ticketListQuery.data?.data.length ?? 0) === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
                No tickets match the current filters.
              </div>
            )}

            {(ticketListQuery.data?.data ?? []).map((ticket) => (
              <button
                key={ticket.supportTicketId}
                type="button"
                onClick={() => selectTicket(ticket.supportTicketId)}
                className="block w-full text-left"
              >
                <div className={ticket.supportTicketId === selectedTicketId ? 'ring-2 ring-blue-500 rounded-2xl' : ''}>
                  <SupportTicketListItem
                    ticket={ticket}
                    managerView
                  />
                </div>
              </button>
            ))}
          </div>

          {totalTickets > currentPerPage && (
            <Pagination
              total={totalTickets}
              page={currentPage}
              pageSize={currentPerPage}
              onPageChange={setPage}
            />
          )}
        </div>
      </ListPageLayout>

      {/* Escape is left to the dialogs opened inside it; closing this unmounts the reply being written. */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Ticket Detail"
        size="xxl"
        closeOnEscape={false}
      >
        {modalOpen && selectedTicketId != null && (
          <SupportTicketThread
            ticketId={selectedTicketId}
            managerView
            onUpdated={onTicketUpdated}
            onMergedTargetClick={selectTicket}
          />
        )}
      </Modal>
    </>
  );
}

function SummaryCard({
  title,
  value,
  highlight = false,
  active = false,
  onClick,
}: {
  title: string;
  value: number;
  highlight?: boolean;
  active?: boolean;
  onClick?: () => void;
}) {
  const baseClasses = active
    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
    : highlight
      ? 'border-blue-200 bg-blue-50'
      : 'border-slate-200 bg-white';

  const card = (
    <Card
      noPadding
      className={`${baseClasses} ${onClick ? 'transition hover:border-blue-400 hover:shadow-sm' : ''}`}
    >
      <CardBody className="px-3 py-2">
        <p className="text-xs font-medium leading-tight text-slate-500">{title}</p>
        <p className="mt-0.5 text-lg font-semibold tabular-nums leading-none text-slate-900">{value}</p>
      </CardBody>
    </Card>
  );

  if (!onClick) return card;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="block w-full rounded-xl text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
    >
      {card}
    </button>
  );
}
